import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  XCircle,
} from "lucide-react";

import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Cluster,
  Input,
  KpiTile,
  LogoMark,
  PageHeading,
  Stack,
  StateDot,
  Table,
  TimerChip,
  type StateDotStatus,
} from "~/components";
import { vars } from "~/styles/tokens.css";

import * as css from "./_app.kitchen-sink.css";

const ALERT_ICON_SIZE = 18;

// Kitchen sink. One-stop reference for every primitive and token
// combination we ship. Useful while building new pages and when the
// team wants to see what's available without grepping the codebase.
export const Route = createFileRoute("/_app/kitchen-sink")({
  component: KitchenSinkPage,
});

function KitchenSinkPage() {
  return (
    <Stack gap="lg">
      <PageHeading
        title="Kitchen sink"
        subtitle="Design-system reference — every primitive, variant, and token in one place."
        actions={
          <Link to="/">
            <Button type="button" variant="secondary">
              Back home
            </Button>
          </Link>
        }
      />

      <BrandSection />
      <TypographySection />
      <ColorTokensSection />
      <ButtonsSection />
      <InputsSection />
      <CardsSection />
      <LayoutSection />
      <AvatarSection />
      <BadgesSection />
      <LiveStatusSection />
      <AlertsSection />
      <TableSection />
      <PageHeadingSection />
    </Stack>
  );
}

// ── Brand mark ─────────────────────────────────────────────────────────

function BrandSection() {
  return (
    <Card>
      <Stack gap="md">
        <h2 className={css.sectionTitle}>Brand mark</h2>
        <p className={css.noteText}>
          Placeholder emblem — olive circle with the letter M. Used in the
          sidebar (36px) and on the login page (48px). Swap for a real
          wordmark once design delivers one.
        </p>
        <Cluster gap="md" align="center">
          <LogoMark size={24} />
          <LogoMark size={36} />
          <LogoMark size={48} />
          <LogoMark size={64} />
        </Cluster>
      </Stack>
    </Card>
  );
}

// ── Typography ─────────────────────────────────────────────────────────

function TypographySection() {
  return (
    <Card>
      <Stack gap="md">
        <h2 className={css.sectionTitle}>Typography</h2>
        <p className={css.noteText}>
          Barlow Condensed is display only — h1–h3, which are 20px and up.
          Below that floor the condensed face reads as cramped, so h4–h6 and
          every body/UI surface are Nunito Sans. Monospace falls back through
          the system stack.
        </p>
        <div>
          <TypeRow label="h1">
            <h1 className={css.typeSample}>The quick brown fox</h1>
          </TypeRow>
          <TypeRow label="h2">
            <h2 className={css.typeSample}>The quick brown fox</h2>
          </TypeRow>
          <TypeRow label="h3">
            <h3 className={css.typeSample}>The quick brown fox</h3>
          </TypeRow>
          <TypeRow label="h4">
            <h4 className={css.typeSample}>The quick brown fox</h4>
          </TypeRow>
          <TypeRow label="h5">
            <h5 className={css.typeSample}>The quick brown fox</h5>
          </TypeRow>
          <TypeRow label="h6">
            <h6 className={css.typeSample}>The quick brown fox</h6>
          </TypeRow>
          <TypeRow label="p">
            <p className={css.typeSample}>
              Body copy for long-form content. Jumps over the lazy dog.
            </p>
          </TypeRow>
          <TypeRow label="small">
            <small>Fine print and secondary info.</small>
          </TypeRow>
          <TypeRow label="strong">
            <strong>Emphasis</strong>
          </TypeRow>
          <TypeRow label="code">
            <code>workflow.definition._fvTaskId</code>
          </TypeRow>
          <TypeRow label="a">
            <a href="#kitchen-sink">A link within the page</a>
          </TypeRow>
        </div>
      </Stack>
    </Card>
  );
}

function TypeRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={css.typeRow}>
      <span className={css.typeLabel}>{label}</span>
      <div>{children}</div>
    </div>
  );
}

// ── Color tokens ───────────────────────────────────────────────────────

function ColorTokensSection() {
  // We render swatches by pulling the CSS var reference from the vars
  // object directly. Names mirror the semantic token keys so the
  // reference is self-documenting.
  const groups: { title: string; swatches: { name: string; color: string }[] }[] = [
    {
      title: "Surface",
      swatches: [
        { name: "background", color: vars.color.background },
        { name: "surface", color: vars.color.surface },
        { name: "surfaceMuted", color: vars.color.surfaceMuted },
      ],
    },
    {
      title: "Text",
      swatches: [
        { name: "textDefault", color: vars.color.textDefault },
        { name: "textMuted", color: vars.color.textMuted },
        { name: "textSubtle", color: vars.color.textSubtle },
        { name: "textInverse", color: vars.color.textInverse },
      ],
    },
    {
      title: "Border",
      swatches: [
        { name: "borderSubtle", color: vars.color.borderSubtle },
        { name: "borderStrong", color: vars.color.borderStrong },
      ],
    },
    {
      title: "Brand — primary",
      swatches: [
        { name: "primary", color: vars.color.primary },
        { name: "primaryHover", color: vars.color.primaryHover },
        { name: "primaryActive", color: vars.color.primaryActive },
      ],
    },
    {
      title: "Status — solid",
      swatches: [
        { name: "danger", color: vars.color.danger },
        { name: "success", color: vars.color.success },
        { name: "warning", color: vars.color.warning },
        { name: "info", color: vars.color.info },
      ],
    },
    {
      title: "Status — tinted surfaces",
      swatches: [
        { name: "dangerSurface", color: vars.color.dangerSurface },
        { name: "successSurface", color: vars.color.successSurface },
        { name: "warningSurface", color: vars.color.warningSurface },
        { name: "infoSurface", color: vars.color.infoSurface },
        { name: "neutralSurface", color: vars.color.neutralSurface },
      ],
    },
    {
      title: "Brand tints + ceremony",
      swatches: [
        { name: "navySurface", color: vars.color.navySurface },
        { name: "oliveSurface", color: vars.color.oliveSurface },
        { name: "gold", color: vars.color.gold },
        { name: "goldSoft", color: vars.color.goldSoft },
      ],
    },
    {
      title: "Liveness + timers",
      swatches: [
        { name: "statusActive", color: vars.color.statusActive },
        { name: "statusWaiting", color: vars.color.statusWaiting },
        { name: "statusScheduled", color: vars.color.statusScheduled },
        { name: "statusIdle", color: vars.color.statusIdle },
        { name: "statusBreach", color: vars.color.statusBreach },
        { name: "timerWarnSurface", color: vars.color.timerWarnSurface },
        { name: "timerBreachSurface", color: vars.color.timerBreachSurface },
      ],
    },
    {
      title: "Sidebar",
      swatches: [
        { name: "sidebarBackground", color: vars.color.sidebarBackground },
        { name: "sidebarSurface", color: vars.color.sidebarSurface },
        { name: "sidebarAccent", color: vars.color.sidebarAccent },
      ],
    },
  ];

  return (
    <Card>
      <Stack gap="md">
        <h2 className={css.sectionTitle}>Color tokens</h2>
        <p className={css.noteText}>
          Semantic tokens only. Components must not reach for raw palette hex
          values — the guardrail is social, not mechanical.
        </p>
        {groups.map((group) => (
          <Stack key={group.title} gap="sm">
            <h3>{group.title}</h3>
            <div className={css.swatchGrid}>
              {group.swatches.map((sw) => (
                <div key={sw.name} className={css.swatch}>
                  <div
                    className={css.swatchChip}
                    style={{ backgroundColor: sw.color }}
                  />
                  <div className={css.swatchMeta}>
                    <span className={css.swatchName}>{sw.name}</span>
                    <span className={css.swatchHex}>{sw.color}</span>
                  </div>
                </div>
              ))}
            </div>
          </Stack>
        ))}
      </Stack>
    </Card>
  );
}

// ── Buttons ────────────────────────────────────────────────────────────

function ButtonsSection() {
  return (
    <Card>
      <Stack gap="md">
        <h2 className={css.sectionTitle}>Buttons</h2>
        <Stack gap="sm">
          <h3>Variants</h3>
          <Cluster gap="sm">
            <Button type="button" variant="primary">Primary</Button>
            <Button type="button" variant="secondary">Secondary</Button>
            <Button type="button" variant="ghost">Ghost</Button>
            <Button type="button" variant="danger">Danger</Button>
          </Cluster>
        </Stack>
        <Stack gap="sm">
          <h3>Sizes — the axis is independent of variant</h3>
          <Cluster gap="sm" align="center">
            <Button type="button" variant="primary" size="md">Medium</Button>
            <Button type="button" variant="primary" size="sm">Small</Button>
            <Button type="button" variant="secondary" size="sm">Small</Button>
            <Button type="button" variant="ghost" size="sm">Small</Button>
          </Cluster>
          <small>
            <code>sm</code> is for dense console surfaces — action bars, row
            actions — where a full-height control would dominate the row.
          </small>
        </Stack>
        <Stack gap="sm">
          <h3>Disabled</h3>
          <Cluster gap="sm">
            <Button type="button" variant="primary" disabled>Primary</Button>
            <Button type="button" variant="secondary" disabled>Secondary</Button>
            <Button type="button" variant="ghost" disabled>Ghost</Button>
            <Button type="button" variant="danger" disabled>Danger</Button>
          </Cluster>
        </Stack>
      </Stack>
    </Card>
  );
}

// ── Inputs ─────────────────────────────────────────────────────────────

function InputsSection() {
  const [value, setValue] = useState("");
  return (
    <Card>
      <Stack gap="md">
        <h2 className={css.sectionTitle}>Inputs</h2>
        <Stack gap="sm">
          <label htmlFor="ks-input-default"><strong>Default</strong></label>
          <Input
            id="ks-input-default"
            placeholder="Type something…"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </Stack>
        <Stack gap="sm">
          <label htmlFor="ks-input-required"><strong>Required + minLength</strong></label>
          <Input
            id="ks-input-required"
            placeholder="At least 3 characters"
            required
            minLength={3}
          />
          <small>
            Try typing 1–2 characters and blurring to see the <code>:user-invalid</code> state.
          </small>
        </Stack>
        <Stack gap="sm">
          <label htmlFor="ks-input-disabled"><strong>Disabled</strong></label>
          <Input id="ks-input-disabled" value="Can't touch this" disabled readOnly />
        </Stack>
      </Stack>
    </Card>
  );
}

// ── Cards ──────────────────────────────────────────────────────────────

function CardsSection() {
  const accents = ["neutral", "primary", "success", "warning", "danger", "info"] as const;
  return (
    <Card>
      <Stack gap="md">
        <h2 className={css.sectionTitle}>Cards</h2>
        <p className={css.noteText}>Optional left-border accent to denote status or urgency.</p>
        <div className={css.gridTwoCol}>
          {accents.map((accent) => (
            <Card key={accent} accent={accent}>
              <Stack gap="xs">
                <strong>accent="{accent}"</strong>
                <small>
                  Cards use the surface token for the background and semantic
                  accent colors for the left border.
                </small>
              </Stack>
            </Card>
          ))}
        </div>
        <h3>Density — independent of accent</h3>
        <div className={css.gridTwoCol}>
          <Card density="standard">
            <strong>density="standard"</strong>
          </Card>
          <Card density="dense" accent="warning">
            <strong>density="dense"</strong>
          </Card>
        </div>
        <p className={css.noteText}>
          Dense is for cards that repeat in quantity on one screen — board
          columns, rail items — where standard padding costs more rows than the
          breathing room is worth.
        </p>
      </Stack>
    </Card>
  );
}

// ── Layout primitives ──────────────────────────────────────────────────

function LayoutSection() {
  return (
    <Card>
      <Stack gap="md">
        <h2 className={css.sectionTitle}>Layout primitives</h2>

        <Stack gap="sm">
          <h3>Stack — vertical, gap="md"</h3>
          <div className={css.miniCard}>
            <Stack gap="md">
              <div className={css.miniCardLabel}>child 1</div>
              <div className={css.miniCardLabel}>child 2</div>
              <div className={css.miniCardLabel}>child 3</div>
            </Stack>
          </div>
        </Stack>

        <Stack gap="sm">
          <h3>Cluster — horizontal wrap, justify="between"</h3>
          <div className={css.miniCard}>
            <Cluster gap="sm" justify="between">
              <Button type="button" variant="secondary">One</Button>
              <Button type="button" variant="secondary">Two</Button>
              <Button type="button" variant="secondary">Three</Button>
              <Button type="button" variant="primary">Go</Button>
            </Cluster>
          </div>
        </Stack>
      </Stack>
    </Card>
  );
}

// ── Avatars ────────────────────────────────────────────────────────────

function AvatarSection() {
  const team = [
    { initials: "TD", name: "Teresa Diep", bg: vars.color.sidebarAccent },
    { initials: "RM", name: "Robert May", bg: vars.color.primary },
    { initials: "CM", name: "Cameron May", bg: vars.color.success },
    { initials: "GM", name: "Garrett May", bg: vars.color.info },
    { initials: "SK", name: "Soo Kim", bg: vars.color.warning },
  ];
  return (
    <Card>
      <Stack gap="md">
        <h2 className={css.sectionTitle}>Avatars</h2>
        <Stack gap="sm">
          <h3>Sizes</h3>
          <Cluster gap="md" align="center">
            <Avatar initials="TD" size={24} />
            <Avatar initials="TD" size={32} />
            <Avatar initials="TD" size={40} />
            <Avatar initials="TD" size={56} />
            <Avatar initials="TD" size={72} />
          </Cluster>
        </Stack>
        <Stack gap="sm">
          <h3>Roster — per-user backgrounds</h3>
          <Cluster gap="md" align="center">
            {team.map((t) => (
              <Cluster key={t.initials} gap="sm" align="center">
                <Avatar initials={t.initials} background={t.bg} />
                <small>{t.name}</small>
              </Cluster>
            ))}
          </Cluster>
        </Stack>
      </Stack>
    </Card>
  );
}

// ── Badges ─────────────────────────────────────────────────────────────

function BadgesSection() {
  const variants = [
    "neutral",
    "success",
    "warning",
    "danger",
    "info",
    "navy",
    "olive",
  ] as const;
  return (
    <Card>
      <Stack gap="md">
        <h2 className={css.sectionTitle}>Badges</h2>
        <Cluster gap="sm">
          {variants.map((v) => (
            <Badge key={v} variant={v}>
              {v}
            </Badge>
          ))}
        </Cluster>
        <Stack gap="sm">
          <h3>In use</h3>
          <Cluster gap="sm">
            <Badge variant="neutral">12 phases</Badge>
            <Badge variant="success">Complete</Badge>
            <Badge variant="warning">In review</Badge>
            <Badge variant="danger">Overdue</Badge>
            <Badge variant="info">Active · Eve</Badge>
          </Cluster>
        </Stack>
      </Stack>
    </Card>
  );
}

// ── Live status: KpiTile, StateDot, TimerChip ──────────────────────────

const MINUTE_MS = 60_000;

function LiveStatusSection() {
  const statuses: StateDotStatus[] = [
    "active",
    "waiting",
    "scheduled",
    "idle",
    "breach",
  ];
  // Fixed elapsed values, one per level, so the page renders the same
  // thing every time — a reference surface should not tick.
  const warnMs = 5 * MINUTE_MS;
  const breachMs = 15 * MINUTE_MS;

  return (
    <Card>
      <Stack gap="md">
        <h2 className={css.sectionTitle}>Live status</h2>
        <p className={css.noteText}>
          The liveness set — what something is doing right now, as opposed to
          Badge, which says how something turned out.
        </p>

        <Stack gap="sm">
          <h3>KpiTile — a strip of headline numbers</h3>
          <Cluster gap="sm">
            <KpiTile value={12} label="New today" />
            <KpiTile value={4} label="Awaiting first touch" />
            <KpiTile value={1} label="Breaches" />
            <KpiTile value={7} label="Signed this week" gold />
          </Cluster>
          <small>
            At most one tile per strip sets <code>gold</code> — ceremony, never
            a functional status.
          </small>
        </Stack>

        <Stack gap="sm">
          <h3>StateDot — 8px liveness dot</h3>
          <Cluster gap="md">
            {statuses.map((s) => (
              <StateDot key={s} status={s} label={s} />
            ))}
          </Cluster>
          <small>
            Only <code>active</code> pulses. Colour is never the only signal:
            the dot carries its status name for assistive tech even unlabelled.
          </small>
        </Stack>

        <Stack gap="sm">
          <h3>TimerChip — nominal / warning / breach</h3>
          <Cluster gap="md" align="center">
            <TimerChip elapsedMs={2 * MINUTE_MS} warnMs={warnMs} breachMs={breachMs} />
            <TimerChip elapsedMs={8 * MINUTE_MS} warnMs={warnMs} breachMs={breachMs} />
            <TimerChip elapsedMs={22 * MINUTE_MS} warnMs={warnMs} breachMs={breachMs} />
            <TimerChip elapsedMs={95 * MINUTE_MS} />
          </Cluster>
          <small>
            Chip geometry appears only past a threshold; a nominal timer is a
            quiet numeral. The last one has no thresholds — it just counts, and
            crosses into h:mm past an hour.
          </small>
        </Stack>
      </Stack>
    </Card>
  );
}

// ── Alerts ─────────────────────────────────────────────────────────────

function AlertsSection() {
  return (
    <Card>
      <Stack gap="md">
        <h2 className={css.sectionTitle}>Alerts</h2>
        <Stack gap="sm">
          <Alert variant="info" icon={<Info size={ALERT_ICON_SIZE} />}>
            <strong>Heads up.</strong> Informational banner — use for neutral
            notices like "deploy is in progress" or "you're viewing stale data."
          </Alert>
          <Alert
            variant="success"
            icon={<CheckCircle2 size={ALERT_ICON_SIZE} />}
          >
            <strong>Saved.</strong> Workflow definition persisted.
          </Alert>
          <Alert
            variant="warning"
            icon={<AlertTriangle size={ALERT_ICON_SIZE} />}
          >
            <strong>Treatment gap.</strong> No appointment in 28 days.
          </Alert>
          <Alert
            variant="danger"
            icon={<XCircle size={ALERT_ICON_SIZE} />}
          >
            <strong>Overdue.</strong> Discovery responses were due 3/24.
          </Alert>
        </Stack>
      </Stack>
    </Card>
  );
}

// ── Tables ─────────────────────────────────────────────────────────────

function TableSection() {
  const rows = [
    { client: "Jose Yanez", phase: "New Case Dev", manager: "Julia Isais", initials: "JI", status: "warning" as const, statusLabel: "Pending Upload" },
    { client: "Estrellita Hernandez", phase: "Treating", manager: "Elizabeth", initials: "EL", status: "info" as const, statusLabel: "Records Received" },
    { client: "Patricia Gobeli", phase: "Litigation", manager: "Gaby Preciado", initials: "GP", status: "danger" as const, statusLabel: "Discovery due 3/24" },
    { client: "Ralph Mason", phase: "Pre-Lit Settled", manager: "Jacqueline Loera", initials: "JL", status: "success" as const, statusLabel: "Letter approved" },
  ];
  return (
    <Card>
      <Stack gap="md">
        <h2 className={css.sectionTitle}>Table</h2>
        <p className={css.noteText}>
          Composed with Badge + Avatar to show how the primitives read together
          in a real row.
        </p>
        <Table>
          <thead>
            <tr>
              <th>Client</th>
              <th>Phase</th>
              <th>Case manager</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.client}>
                <td><strong>{r.client}</strong></td>
                <td>{r.phase}</td>
                <td>
                  <Cluster gap="sm" align="center">
                    <Avatar initials={r.initials} size={24} />
                    <span>{r.manager}</span>
                  </Cluster>
                </td>
                <td>
                  <Badge variant={r.status}>{r.statusLabel}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Stack>
    </Card>
  );
}

// ── PageHeading ────────────────────────────────────────────────────────

function PageHeadingSection() {
  return (
    <Card>
      <Stack gap="md">
        <h2 className={css.sectionTitle}>PageHeading</h2>
        <p className={css.noteText}>
          Every page should lead with one of these. The kitchen-sink page
          itself uses PageHeading — the examples below render inside a
          bordered container to show the component in isolation.
        </p>

        <div className={css.miniCard}>
          <PageHeading title="Title only" />
        </div>

        <div className={css.miniCard}>
          <PageHeading
            title="Title + subtitle"
            subtitle="Secondary line that explains the page or surfaces a meta detail."
          />
        </div>

        <div className={css.miniCard}>
          <PageHeading
            title="Title + subtitle + actions"
            subtitle="The full pattern most production pages use."
            actions={
              <>
                <Button type="button" variant="secondary">Secondary</Button>
                <Button type="button" variant="primary">Primary</Button>
              </>
            }
          />
        </div>
      </Stack>
    </Card>
  );
}
